import { useEffect, useRef } from "react";
import useRealEstateDetail from "./QueryHooks/useRealEstateDetail";
const TRACKED = ['meta[name="description"]', 'meta[property="og:title"]', 'meta[property="og:description"]', 'meta[property="og:image"]', 'meta[name="twitter:image"]', 'meta[property="og:url"]', 'meta[property="og:type"]'];
type Snapshot = {
  title: string;
  metas: Record<string, string | null>;
};
const capture = (): Snapshot => ({
  title: document.title,
  metas: Object.fromEntries(TRACKED.map(selector => [selector, document.querySelector(selector)?.getAttribute("content") ?? null]))
});
const setMeta = (selector: string, value: string) => {
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    const [, key, name] = selector.match(/\[(name|property)="([^"]+)"\]/) ?? [];
    element.setAttribute(key, name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", value);
};

// The listing dialog opens over any page, so it swaps in the listing's tags
// while open and puts the page's own tags back when it closes.
export default function useMetadata(realEstateId: string, isModalOpen?: boolean) {
  const {
    data: realEstateData
  } = useRealEstateDetail({
    realEstateSlug: realEstateId
  });
  const snapshotRef = useRef<Snapshot | null>(null);
  const updateMetaTags = () => {
    if (!realEstateData) return;
    if (!snapshotRef.current) snapshotRef.current = capture();
    if (!realEstateData?.data) {
      document.title = `CasaStep | Investe no teu novo imovel`;
      return;
    }
    const {
      realEstate
    } = realEstateData.data;
    document.title = `CasaStep | ${realEstate?.title}`;
    const description = realEstate.description?.substring(0, 160) || "Veja os detalhes do imóvel";
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    const imageUrl = realEstate.thumbnails?.[0];
    if (!imageUrl) return;
    setMeta('meta[property="og:image"]', imageUrl);
    setMeta('meta[name="twitter:image"]', imageUrl);
    setMeta('meta[property="og:title"]', `${realEstate?.title} | CasaStep`);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', window.location.href);
    setMeta('meta[property="og:type"]', "website");
  };
  const restoreMetaTags = () => {
    const snapshot = snapshotRef.current;
    if (!snapshot) return;
    document.title = snapshot.title;
    for (const selector of TRACKED) {
      const value = snapshot.metas[selector];
      const element = document.querySelector(selector);
      if (value === null) element?.remove();else element?.setAttribute("content", value);
    }
    snapshotRef.current = null;
  };
  useEffect(() => {
    if (realEstateId) {
      updateMetaTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realEstateData, isModalOpen, realEstateId]);
  useEffect(() => {
    if (isModalOpen === false) {
      restoreMetaTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);
}